import { useLocation, useNavigate, useParams } from "react-router-dom";
import GoBack from "../components/GoBack";
import { useEffect, useRef, useState } from "react";
import useFlow from "../hooks/useFlow";
import { toast } from "react-toastify";
import PhoneInputWithNumpad from "../components/PhoneInputWithNumpad";
import ConfirmationModal from "../components/ConfirmationModal";
import useStore from "../hooks/useStore";

export default function ClassEventJoinPhone({ type }) {
  const {
    findClientByPhone,
    selectedSchedule,
    joinSchedule,
    openRegisterModal,
    loadScheduleById,
    setShowMapResourceModal,
    setShowOfferingModal,
    setResource,
    setPendingJoin,
    setShowEventNotificationModal,
    eventModalEnabled,
    locationPreferences,
    registeredClientPhone,
    setRegisteredClientPhone,
  } = useFlow();

  const {
    setShowPlansOfferModal,
    setSelectedClient,
    setShowEventDetailModal,
    getPlansForSchedule,
    purchaseFreeItem,
    showPlansOfferModal,
    showPopularItemModal,
    showCheckoutModal,
  } = useStore();

  const navigate = useNavigate();
  const location = useLocation();
  const { classId } = useParams();

  const [clients, setClients] = useState([]);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showClassFullModal, setShowClassFullModal] = useState(false);
  const [showSuspendedModal, setShowSuspendedModal] = useState(false);
  const [suspendedMessage, setSuspendedMessage] = useState("");

  const phoneInputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      phoneInputRef.current.focus();
    }, 500);
  }, []);

  useEffect(() => {
    if (!classId) return navigate(-1);
    if (!selectedSchedule || selectedSchedule?.id != classId)
      loadScheduleById(classId);
  }, [classId, selectedSchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const findClients = async (phone) => {
    try {
      // Remove mask special characters
      const unmaskedPhone = phone.replace(/[^\d]/g, "");

      // Validate phone number
      const phoneRegex = /^(\+1|1)?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
      if (!phoneRegex.test(unmaskedPhone)) {
        toast.error("Please enter a valid American phone number.");
        return;
      }

      setLoading(true);
      setClients([]);
      const data = await findClientByPhone(unmaskedPhone);

      setClients(data);
      setLoading(false);
      setSearched(true);
    } catch (e) {
      setLoading(false);
      const msg = e?.message?.trim();
      const isNoResults =
        !msg ||
        msg.includes("not defined") ||
        msg === "[object Object]";
      toast.error(
        isNoResults
          ? "No results found for this phone number. Please check the number and try again."
          : `Something went wrong! ${msg}`
      );
    }
  };

  const findAndJoin = async (phone) => {
    try {
      const unmaskedPhone = phone.replace(/[^\d]/g, "");
      setLoading(true);
      setClients([]);
      const data = await findClientByPhone(unmaskedPhone);
      setClients(data);
      setLoading(false);
      setSearched(true);
      if (data.length === 1) {
        await join(data[0]);
      }
    } catch (e) {
      setLoading(false);
      toast.error(`Something went wrong! ${e?.message}`);
    }
  };

  useEffect(() => {
    const anyModalOpen = showPlansOfferModal || showPopularItemModal || showCheckoutModal;
    if (!anyModalOpen && registeredClientPhone) {
      const phoneToSearch = registeredClientPhone;
      setRegisteredClientPhone(null);
      setPhone(phoneToSearch);
      findAndJoin(phoneToSearch);
    }
  }, [showPlansOfferModal, showPopularItemModal, showCheckoutModal, registeredClientPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCheckInAndJoin = (client) => {
    const checkedIn = location?.state?.allCheckins || [];
    return {
      join: checkedIn.some((checkin) => checkin.clientId === client.id),
      checkin: checkedIn.some(
        (checkin) => checkin.clientId === client.id && checkin.checkin
      ),
    };
  };

  const join = async (client) => {
    try {
      setLoadingJoin(true);
      const data = await joinSchedule(selectedSchedule.id, client);
      setLoadingJoin(false);
      setResource({
        name: selectedSchedule.resourceName,
        id: selectedSchedule.resourceId,
      });
      if (
        eventModalEnabled &&
        !document.cookie.includes(`votedClientId_${client.id}`)
      )
        setShowEventNotificationModal(true);
      else if (data?.offering && locationPreferences?.offeringEnabled !== false) {
        setShowOfferingModal(true);
      } else {
        setShowMapResourceModal(true);
      }
      navigate(-3);
    } catch (e) {
      if (e?.code === "ClassFull") {
        setLoadingJoin(false);
        setShowClassFullModal(true);
        return;
      }
      if (e?.code === "ClientSuspended") {
        // Account is on hold — never push a suspended client into checkout
        // (they'd be charged for a pass that can't book them in).
        setLoadingJoin(false);
        setSuspendedMessage(
          e?.message ||
            "Your account is on hold. Please see a staff member to check you in."
        );
        setShowSuspendedModal(true);
        return;
      }
      if (e?.code === "ClassRequiresPayment") {
        if (!client.siteId) {
          try {
            const plans = await getPlansForSchedule(
              selectedSchedule.scheduleId || selectedSchedule.id
            );
            const freePlan = Array.isArray(plans)
              ? plans.find((p) => p.price === 0)
              : null;
            if (freePlan) {
              await purchaseFreeItem(client, freePlan);
              await joinSchedule(selectedSchedule.id, client);
              setLoadingJoin(false);
              setResource({
                name: selectedSchedule.resourceName,
                id: selectedSchedule.resourceId,
              });
              navigate(-3);
              if (
                eventModalEnabled &&
                !document.cookie.includes(`votedClientId_${client.id}`)
              )
                setShowEventNotificationModal(true);
              else setShowMapResourceModal(true);
              return;
            }
          } catch {
            // free-plan shortcut failed — fall through to modal flow
          }
          setLoadingJoin(false);
          setPendingJoin({ scheduleId: selectedSchedule.id, client });
          setSelectedClient(client);
        } else {
          setLoadingJoin(false);
        }
        if (type === "events") setShowEventDetailModal(true);
        else setShowPlansOfferModal(true);
      } else {
        setLoadingJoin(false);
        toast.error(`Something went wrong! ${e?.message}`);
      }
    }
  };

  const createAccountHandler = () => {
    openRegisterModal();
  };
  return (
    <>
      <GoBack />

      <div className="container-fluid d-block" id="app-class-details">
        <div className="primary-screen">
          <div className="row flex-nowrap sidebar">
            {selectedSchedule && (
              <div
                className="col-auto col-md-3 px-0 bg-white mx-auto d-none d-md-block min-vh-100"
                style={{ maxWidth: 500 }}
              >
                <div
                  className="align-items-center px-3 pt-2 min-vh-100 text-center h-100 position-fixed"
                  style={{ width: "inherit", maxWidth: "inherit" }}
                >
                  <div className="row text-center align-items-center h-100">
                    <div className="col-md-12 m-auto">
                      <div
                        className="ratio ratio-1x1 rounded-4"
                        style={{
                          background: `url(${selectedSchedule.staffImage}) no-repeat center`,
                          backgroundSize: "cover",
                        }}
                      ></div>
                      <div className="p-4" id="class_info">
                        <p className="p-0 m-0">
                          {type === "event" &&
                            selectedSchedule.startDateFormatted + ", "}
                          {selectedSchedule.startTimeFormatted}
                          {" - "}
                          {selectedSchedule.endTimeFormatted}
                        </p>
                        <h3 className="py-2 m-0 fw-bold">
                          {selectedSchedule.name}
                        </h3>
                        <p className="p-0 m-0 item-info">
                          <small>with</small> {selectedSchedule.staffName}{" "}
                          {selectedSchedule.resourceName && <small>in</small>}{" "}
                          {selectedSchedule.resourceName}
                        </p>
                      </div>
                      {type == "events" && (
                        <p className="opacity-0">
                          <a style={{ cursor: "default" }}>
                            Read about the event &gt;
                          </a>
                        </p>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-lg mt-5 opacity-0"
                        style={{ cursor: "default" }}
                      >
                        Join {type === "event" ? "Event" : "Class"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="col py-3 text-center d-block" id="enrolled-listing">
              <div id="search" className="d-block">
                <div className="secondary-screen p-md-5">
                  <div className="row text-center pt-5 pt-md-0 pb-5">
                    <div className="col-md-8 m-auto pt-5 pt-md-0">
                      <h3 className="py-2 fw-light">
                        Let&apos;s find your account <br />
                        <b className="fw-bold">enter your phone number</b>
                      </h3>
                    </div>
                  </div>
                  <div className="row justify-content-center pb-5 mb-5">
                    <div className="col-12 col-md-8 col-lg-4 p-0 mb-5">
                      <PhoneInputWithNumpad
                        value={phone}
                        onChange={setPhone}
                        placeholder="(512) 555-0123"
                        disabled={loading}
                        showNumpad={locationPreferences?.showPhoneNumpad}
                        inputRef={phoneInputRef}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") findClients(phone);
                        }}
                        onComplete={(val) =>
                          findClients((val || "").replace(/\D/g, ""))
                        }
                      />
                    </div>
                    <div className="col-auto">
                      <a
                        className="btn btn-lg btn-primary"
                        data-testid="phone-search-btn"
                        onClick={() => findClients(phone)}
                      >
                        {loading ? (
                          <span
                            className="spinner-grow spinner-grow-sm"
                            aria-hidden="true"
                          />
                        ) : (
                          "Search"
                        )}
                      </a>
                    </div>
                    <div className="pt-5 mt-5 d-block">
                      {clients.map((client, idx) => (
                        <div
                          className="row py-2 d-flex student justify-content-center"
                          key={idx}
                        >
                          <div className="col-8 p-4 bg-white shadow-sm rounded-4 border">
                            <p className="float-start p-0 m-0 student-name">
                              {client.name}
                              {client.siteId && (
                                <span
                                  className="badge text-bg-secondary ms-3"
                                  style={{
                                    fontSize: 10,
                                    verticalAlign: "middle",
                                  }}
                                >
                                  remote: {client.siteId}
                                </span>
                              )}
                            </p>
                            {isCheckInAndJoin(client).checkin ? (
                              <button
                                className="btn btn-lg btn-outline-secondary float-end"
                                disabled
                              >
                                Joined and checked-in
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-lg join-with-check-in btn-outline-primary float-end"
                                data-testid="join-checkin-btn"
                                onClick={() => join(client)}
                                disabled={loadingJoin}
                              >
                                {loadingJoin ? (
                                  <span
                                    className="spinner-grow spinner-grow-sm"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  "Check-in"
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {searched && clients.length === 0 && (
                        <div className="row pt-5 text-center">
                          <div className="col-12">
                            <h3 className="py-2 fw-light">
                              <p className="w-100">
                                We couldnt find an account with that phone
                                number
                              </p>
                            </h3>
                          </div>
                        </div>
                      )}

                      <div className="row pt-5 text-center">
                        <div className="col-12">
                          <a
                            className="sign-up-modal btn btn-outline-primary btn-lg"
                            data-testid="signup-btn"
                            onClick={createAccountHandler}
                          >
                            Create an account
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={showClassFullModal}
        title="Class is full"
        body="This class is full. Please ask a staff member to check you in."
        btnYesText="Got it"
        handleClose={() => setShowClassFullModal(false)}
        handlerYes={() => setShowClassFullModal(false)}
      />
      <ConfirmationModal
        show={showSuspendedModal}
        title="Account on hold"
        body={suspendedMessage}
        btnYesText="Got it"
        handleClose={() => setShowSuspendedModal(false)}
        handlerYes={() => setShowSuspendedModal(false)}
      />
    </>
  );
}
